import React, { useEffect, useMemo, useState } from "react";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  ReferenceLine,
} from "recharts";
/* ======================================================================
   MLB RANGE OF OUTCOMES
   - Uses separate batter + pitcher projections
   - Uses separate batter + pitcher point distribution JSONs
   - DK / FD / Both toggle
   - Batters / Pitchers / Both toggle
   - Position tabs
   - Slate filter via site_ids.json
   - Team logos
   - 1x, 1.5x, 2x, 2.5x, 3x, 4x multiplier thresholds
   - Good/Boom/Smash/Bust + RO Score using MLB-specific raw + salary targets
   ====================================================================== */
const BATTERS_SOURCE =
  import.meta.env?.VITE_MLB_BATTERS_URL ||
  "/data/mlb/latest/batter_projections.json";
const PITCHERS_SOURCE =
  import.meta.env?.VITE_MLB_PITCHERS_URL ||
  "/data/mlb/latest/pitcher_projections.json";
const SITE_ID_SOURCE =
  import.meta.env?.VITE_MLB_SITE_ID_URL ||
  "/data/mlb/latest/site_ids.json";
const BATTER_POINT_DISTS =
  import.meta.env?.VITE_MLB_BATTER_POINT_DISTS_URL ||
  "/data/mlb/latest/batter_point_distributions.json";
const PITCHER_POINT_DISTS =
  import.meta.env?.VITE_MLB_PITCHER_POINT_DISTS_URL ||
  "/data/mlb/latest/pitcher_point_distributions.json";
const SITES = {
  dk: { key: "dk", label: "DK", logo: "/logos/dk.png" },
  fd: { key: "fd", label: "FD", logo: "/logos/fd.png" },
  both: { key: "both", label: "Both" },
};
const ALIASES = {
  player: ["player", "name", "pitcher", "player name"],
  team: ["team", "teamabbrev", "tm"],
  opp: ["opp", "opponent"],
  dk_pos: ["dk pos", "dk position", "dkpos", "pos", "position"],
  fd_pos: ["fd pos", "fd position", "fdpos"],
  dk_sal: ["dk sal", "dk salary", "draftkings salary", "dk$"],
  dk_proj: ["dk proj", "dk projection", "dk pts", "draftkings proj"],
  dk_val: ["dk val", "dk value"],
  dk_pown: ["dk pown%", "dk pown", "dk own%", "dk ownership%", "dk_pown"],
  dk_floor: ["dk floor", "dk_floor"],
  dk_ceiling: ["dk ceiling", "dk_ceiling"],
  dk_optimal: ["dk optimal", "dk opt", "dk_optimal", "dk optimal%", "dk opt%"],
  dk_rtg: ["dk rtg", "dk rating", "dk_rating"],
  fd_sal: ["fd sal", "fd salary", "fanduel salary", "fd$"],
  fd_proj: ["fd proj", "fd projection", "fd pts", "fanduel proj"],
  fd_val: ["fd val", "fd value"],
  fd_pown: ["fd pown%", "fd pown", "fd own%", "fd ownership%", "fd_pown"],
  fd_floor: ["fd floor", "fd_floor"],
  fd_ceiling: ["fd ceiling", "fd_ceiling"],
  fd_optimal: ["fd optimal", "fd opt", "fd_optimal", "fd optimal%", "fd opt%"],
  fd_rtg: ["fd rtg", "fd rating", "fd_rating"],
};
const MULTS = [1, 1.5, 2, 2.5, 3, 4];
const lc = (s) => String(s ?? "").toLowerCase().trim();
const buildLowerMap = (row) => {
  const low = {};
  for (const [k, v] of Object.entries(row || {})) low[lc(k)] = v;
  return low;
};
const firstKey = (obj, names) => names.map(lc).find((n) => n in obj);
const pick = (obj, names) => obj[firstKey(obj, names) ?? ""];
const num = (v) => {
  if (v === null || v === undefined || v === "") return null;
  const n = Number(String(v).replace(/[,$%\s]/g, ""));
  return Number.isFinite(n) ? n : null;
};
const pctNum = (v) => {
  const n = num(v);
  if (n === null) return null;
  return Math.max(0, Math.min(100, n));
};
const fmtMoney0 = (v) => (num(v) === null ? "" : `$${num(v).toLocaleString()}`);
const fmt1 = (v) => (num(v) === null ? "" : num(v).toFixed(1));
const fmtPct1 = (v) => (num(v) === null ? "" : `${num(v).toFixed(1)}%`);
const teamLogo = (team) => `/logos/mlb/${String(team || "").toUpperCase()}.png`;
const keynorm = (s) =>
  String(s || "")
    .toLowerCase()
    .trim()
    .replace(/\s+/g, " ")
    .replace(/[^a-z0-9 ]/g, "");
function normalizeParts(raw) {
  return String(raw || "")
    .toUpperCase()
    .split(/[\/,;|\s]+/g)
    .map((p) => p.trim())
    .filter(Boolean)
    .map((p) => {
      if (p === "LF" || p === "CF" || p === "RF") return "OF";
      if (p === "SP" || p === "RP") return "P";
      return p;
    });
}
function getPosForSite(row, site) {
  if (site === "dk") return normalizeParts(row.dk_pos || row.pos || "").join("/");
  if (site === "fd") return normalizeParts(row.fd_pos || row.pos || "").join("/");
  const dkParts = normalizeParts(row.dk_pos || "");
  const fdParts = normalizeParts(row.fd_pos || "");
  const fallback = normalizeParts(row.pos || "");
  const merged = [...new Set([...(dkParts.length ? dkParts : []), ...(fdParts.length ? fdParts : [])])];
  return merged.length ? merged.join("/") : fallback.join("/");
}
async function fetchJsonGuard(url) {
  const r = await fetch(`${url}?_=${Date.now()}`, { cache: "no-store" });
  if (!r.ok) throw new Error(`HTTP ${r.status}`);
  const j = await r.json();
  return Array.isArray(j) ? j : j?.rows || j?.data || j?.items || [];
}
function useAnyJson(url) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      setErr("");
      try {
        const r = await fetch(`${url}?_=${Date.now()}`, { cache: "no-store" });
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const j = await r.json();
        if (alive) setData(j);
      } catch (e) {
        if (alive) setErr(String(e.message || e));
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [url]);
  return { data, loading, err };
}
function normalizeCommon(row) {
  const low = buildLowerMap(row);
  const dkPosRaw = (pick(low, ALIASES.dk_pos) || "").toUpperCase();
  const fdPosRaw = (pick(low, ALIASES.fd_pos) || "").toUpperCase();
  const genericPos = (dkPosRaw || fdPosRaw || "").toUpperCase();
  return {
    player: pick(low, ALIASES.player),
    dk_pos: dkPosRaw,
    fd_pos: fdPosRaw,
    pos: genericPos,
    team: (pick(low, ALIASES.team) || "").toUpperCase(),
    opp: (pick(low, ALIASES.opp) || "").toUpperCase(),
    dk_sal: num(pick(low, ALIASES.dk_sal)),
    dk_proj: num(pick(low, ALIASES.dk_proj)),
    dk_val: num(pick(low, ALIASES.dk_val)),
    dk_pown: pctNum(pick(low, ALIASES.dk_pown)),
    dk_floor: num(pick(low, ALIASES.dk_floor)),
    dk_ceiling: num(pick(low, ALIASES.dk_ceiling)),
    dk_optimal: pctNum(pick(low, ALIASES.dk_optimal)),
    dk_rtg: num(pick(low, ALIASES.dk_rtg)),
    fd_sal: num(pick(low, ALIASES.fd_sal)),
    fd_proj: num(pick(low, ALIASES.fd_proj)),
    fd_val: num(pick(low, ALIASES.fd_val)),
    fd_pown: pctNum(pick(low, ALIASES.fd_pown)),
    fd_floor: num(pick(low, ALIASES.fd_floor)),
    fd_ceiling: num(pick(low, ALIASES.fd_ceiling)),
    fd_optimal: pctNum(pick(low, ALIASES.fd_optimal)),
    fd_rtg: num(pick(low, ALIASES.fd_rtg)),
  };
}
function normalizeBatter(row) {
  return { type: "BAT", ...normalizeCommon(row) };
}
function normalizePitcher(row) {
  const base = normalizeCommon(row);
  return {
    type: "P",
    ...base,
    dk_pos: (base.dk_pos || "P").toUpperCase(),
    fd_pos: (base.fd_pos || "P").toUpperCase(),
    pos: "P",
  };
}
function useMlbCombined(battersUrl, pitchersUrl) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      setErr("");
      try {
        const [batRaw, pitRaw] = await Promise.all([
          fetchJsonGuard(battersUrl),
          fetchJsonGuard(pitchersUrl),
        ]);
        const bats = (batRaw || []).map(normalizeBatter).filter((r) => r.player);
        const pits = (pitRaw || []).map(normalizePitcher).filter((r) => r.player);
        if (alive) setRows([...bats, ...pits]);
      } catch (e) {
        if (alive) setErr(String(e.message || e));
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [battersUrl, pitchersUrl]);
  return { rows, loading, err };
}
function buildSiteIdIndex(raw) {
  const out = { dk: new Map(), fd: new Map() };
  if (!raw || typeof raw !== "object") return out;
  for (const siteKey of ["dk", "fd"]) {
    const arr = Array.isArray(raw?.[siteKey]) ? raw[siteKey] : [];
    for (const row of arr) {
      const nameKey = keynorm(row?.name);
      const teamKey = String(row?.team || "").toUpperCase().trim();
      const comboKey = `${nameKey}__${teamKey}`;
      out[siteKey].set(comboKey, {
        id: row?.id ?? null,
        salary: num(row?.salary),
        early_id: row?.early_id ?? null,
        early_salary: num(row?.early_salary),
        late_id: row?.late_id ?? null,
        late_salary: num(row?.late_salary),
      });
    }
  }
  return out;
}
function getSiteIdEntry(siteIdIndex, row, site) {
  const siteKey = site === "fd" ? "fd" : "dk";
  const nameKey = keynorm(row?.player);
  const teamKey = String(row?.team || "").toUpperCase().trim();
  return siteIdIndex?.[siteKey]?.get(`${nameKey}__${teamKey}`) ?? null;
}
function getSlateSalary(entry, slate) {
  if (!entry) return null;
  if (slate === "early") return num(entry.early_salary);
  if (slate === "late") return num(entry.late_salary);
  return num(entry.salary);
}
function hasSlateEntry(entry, slate) {
  if (!entry) return false;
  if (slate === "early") return !!entry.early_id && num(entry.early_salary) !== null;
  if (slate === "late") return !!entry.late_id && num(entry.late_salary) !== null;
  if (slate === "main") {
    const sal = num(entry.salary);
    return !!entry.id && sal !== null && sal > 0 && sal < 50000;
  }
  if (slate === "all") {
    return (
      (!!entry.id && num(entry.salary) !== null && num(entry.salary) > 0) ||
      (!!entry.early_id && num(entry.early_salary) !== null && num(entry.early_salary) > 0) ||
      (!!entry.late_id && num(entry.late_salary) !== null && num(entry.late_salary) > 0)
    );
  }
  return true;
}
function rowMatchesSlate(siteIdIndex, row, site, slate) {
  if (slate === "all") return true;
  if (site === "dk" || site === "fd") {
    return hasSlateEntry(getSiteIdEntry(siteIdIndex, row, site), slate);
  }
  return (
    hasSlateEntry(getSiteIdEntry(siteIdIndex, row, "dk"), slate) ||
    hasSlateEntry(getSiteIdEntry(siteIdIndex, row, "fd"), slate)
  );
}
function getRenderedSalary(siteIdIndex, row, site, slate, baseValue) {
  if (slate === "all") return num(baseValue);
  const entry = getSiteIdEntry(siteIdIndex, row, site);
  return getSlateSalary(entry, slate) ?? num(baseValue);
}
function toMetaUrl(urlLike) {
  return String(urlLike || "").replace(/[^/]+$/, "meta.json");
}
function parseMetaToDate(meta) {
  const iso =
    meta?.updated_iso ||
    meta?.updated_utc ||
    meta?.source_mtime_iso ||
    meta?.last_updated ||
    meta?.timestamp;
  if (iso) {
    const d = new Date(iso);
    if (!isNaN(d)) return d;
  }
  const epoch = meta?.updated_epoch ?? meta?.epoch;
  if (Number.isFinite(epoch)) {
    const d = new Date(epoch * (epoch > 10_000_000_000 ? 1 : 1000));
    if (!isNaN(d)) return d;
  }
  return null;
}
function useLastUpdatedFromSources(a, b) {
  const [date, setDate] = useState(null);
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        for (const u of [a, b].filter(Boolean)) {
          const r = await fetch(`${toMetaUrl(u)}?_=${Date.now()}`, { cache: "no-store" });
          if (!r.ok) continue;
          const d = parseMetaToDate(await r.json());
          if (d && alive) {
            setDate(d);
            return;
          }
        }
      } catch (_) {}
    })();
    return () => {
      alive = false;
    };
  }, [a, b]);
  return date
    ? date.toLocaleString(undefined, {
        month: "numeric",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
      })
    : null;
}
function cleanBins(bins) {
  if (!Array.isArray(bins)) return [];
  return bins
    .map((b) => ({
      bin_start: Number(b?.bin_start),
      bin_end: Number(b?.bin_end),
      prob: Number(b?.prob),
    }))
    .filter(
      (b) =>
        Number.isFinite(b.bin_start) &&
        Number.isFinite(b.bin_end) &&
        Number.isFinite(b.prob) &&
        b.bin_end > b.bin_start
    )
    .sort((a, z) => a.bin_start - z.bin_start);
}
function probGte(bins, target) {
  const t = Number(target);
  if (!Number.isFinite(t)) return null;
  let total = 0;
  for (const b of cleanBins(bins)) {
    const a = b.bin_start;
    const z = b.bin_end;
    const p = b.prob;
    if (z <= t) continue;
    if (a >= t) total += p;
    else {
      const width = z - a;
      total += p * ((z - t) / width);
    }
  }
  return Math.max(0, Math.min(1, total)) * 100;
}
function probLte(bins, target) {
  const t = Number(target);
  if (!Number.isFinite(t)) return null;
  let total = 0;
  for (const b of cleanBins(bins)) {
    const a = b.bin_start;
    const z = b.bin_end;
    const p = b.prob;
    if (a >= t) continue;
    if (z <= t) total += p;
    else {
      const width = z - a;
      total += p * ((t - a) / width);
    }
  }
  return Math.max(0, Math.min(1, total)) * 100;
}
function buildBinsFromFloorCeil(mean, floor, ceiling, isBatter) {
  const m = num(mean);
  const f = num(floor);
  const c = num(ceiling);
  if (m === null || f === null || c === null || c <= f) return [];
  const std = Math.max(0.5, (c - f) / 4);
  const minX = Math.max(0, Math.floor(f - std * 1.5));
  const maxX = Math.ceil(c + std * 1.5);
  const width = isBatter ? 1 : 2;
  const erf = (x) => {
    const sign = x < 0 ? -1 : 1;
    const a1 = 0.254829592;
    const a2 = -0.284496736;
    const a3 = 1.421413741;
    const a4 = -1.453152027;
    const a5 = 1.061405429;
    const p = 0.3275911;
    const ax = Math.abs(x);
    const t = 1 / (1 + p * ax);
    const y =
      1 -
      (((((a5 * t + a4) * t + a3) * t + a2) * t + a1) * t) * Math.exp(-ax * ax);
    return sign * y;
  };
  const cdf = (x) => 0.5 * (1 + erf((x - m) / (std * Math.SQRT2)));
  const bins = [];
  for (let a = minX; a < maxX; a += width) {
    const z = Math.min(a + width, maxX);
    bins.push({ bin_start: a, bin_end: z, prob: Math.max(0, cdf(z) - cdf(a)) });
  }
  const sum = bins.reduce((acc, b) => acc + (Number(b.prob) || 0), 0);
  if (sum > 0) bins.forEach((b) => (b.prob = b.prob / sum));
  return bins;
}
function buildDistIndex(raw) {
  const pd = raw && typeof raw === "object" ? raw : null;
  if (!pd) return { dk: {}, fd: {} };
  const pickIdx = (obj) => {
    const out = {};
    if (!obj || typeof obj !== "object") return out;
    for (const [name, entry] of Object.entries(obj)) {
      const k = keynorm(name);
      const bins = Array.isArray(entry) ? entry : Array.isArray(entry?.bins) ? entry.bins : null;
      if (k && bins) out[k] = bins;
    }
    return out;
  };
  return {
    dk: pickIdx(pd.dk ?? pd.DK ?? {}),
    fd: pickIdx(pd.fd ?? pd.FD ?? {}),
  };
}
function siteKeysFor(site) {
  if (site === "dk") return ["dk"];
  if (site === "fd") return ["fd"];
  return ["dk", "fd"];
}
const TARGET_PROFILES = {
  dk: {
    BAT: {
      goodRaw: 8,
      boomRaw: 14,
      smashRaw: 20,
      bustRaw: 3,
      goodX: 2.0,
      boomX: 3.0,
      smashX: 4.0,
      bustX: 1.0,
    },
    P: {
      goodRaw: 16,
      boomRaw: 24,
      smashRaw: 32,
      bustRaw: 10,
      goodX: 2.0,
      boomX: 2.5,
      smashX: 3.2,
      bustX: 1.5,
    },
  },
  fd: {
    BAT: {
      goodRaw: 9,
      boomRaw: 16,
      smashRaw: 24,
      bustRaw: 3,
      goodX: 2.5,
      boomX: 4.0,
      smashX: 6.0,
      bustX: 1.25,
    },
    P: {
      goodRaw: 28,
      boomRaw: 40,
      smashRaw: 52,
      bustRaw: 18,
      goodX: 3.0,
      boomX: 4.2,
      smashX: 5.2,
      bustX: 2.0,
    },
  },
};
function targetFromProfile(salary, rawTarget, multTarget) {
  const sal = num(salary);
  if (!sal) return null;
  return Math.max(rawTarget, (sal / 1000) * multTarget);
}
function getOutcomeTargets(row, siteKey, salary) {
  const site = siteKey === "fd" ? "fd" : "dk";
  const type = row.type === "P" ? "P" : "BAT";
  const p = TARGET_PROFILES[site][type];
  return {
    goodTarget: targetFromProfile(salary, p.goodRaw, p.goodX),
    boomTarget: targetFromProfile(salary, p.boomRaw, p.boomX),
    smashTarget: targetFromProfile(salary, p.smashRaw, p.smashX),
    bustTarget: targetFromProfile(salary, p.bustRaw, p.bustX),
    goodLabel: "Good",
    boomLabel: "Boom",
    smashLabel: "Smash",
    bustLabel: "Bust",
  };
}
function rangeScore(row, metrics) {
  const good = num(metrics.good) ?? 0;
  const boom = num(metrics.boom) ?? 0;
  const smash = num(metrics.smash) ?? 0;
  const bust = num(metrics.bust) ?? 0;
  const pown = num(metrics.pown) ?? 0;
  const opt = num(metrics.opt) ?? 0;
  const lev = opt - pown;
  const isPitcher = row.type === "P";
  const goodW = isPitcher ? 0.35 : 0.18;
  const boomW = isPitcher ? 0.65 : 0.55;
  const smashW = isPitcher ? 0.55 : 0.75;
  const bustW = isPitcher ? 0.30 : 0.22;
  const levW = 0.25;
  return Math.max(0, good * goodW + boom * boomW + smash * smashW + lev * levW - bust * bustW);
}
function heatTextColor(min, max, value, dir, isDark) {
  const v = num(value);
  if (v === null || min == null || max == null || min === max || !dir) return null;
  let t = (v - min) / (max - min);
  t = Math.max(0, Math.min(1, t));
  if (dir === "lower") t = 1 - t;
  if (t < 0.5) {
    const u = t / 0.5;
    const h = 8 + u * 42;
    const s = isDark ? 90 : 85;
    const l = isDark ? 70 - u * 8 : 48 - u * 4;
    return `hsl(${h} ${s}% ${l}%)`;
  }
  const u = (t - 0.5) / 0.5;
  const h = 50 + u * 70;
  const s = isDark ? 88 : 80;
  const l = isDark ? 62 + u * 8 : 44 - u * 4;
  return `hsl(${h} ${s}% ${l}%)`;
}
function dirForKey(k) {
  if (["sal", "pown", "bust"].includes(k)) return "lower";
  if (
    [
      "proj",
      "val",
      "opt",
      "lev",
      "rtg",
      "x1",
      "x1_5",
      "x2",
      "x2_5",
      "x3",
      "x4",
      "good",
      "boom",
      "smash",
      "roScore",
    ].includes(k)
  )
    return "higher";
  return null;
}
const escapeCSV = (s) => {
  const v = String(s ?? "");
  return /[",\r\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v;
};
function downloadCSV(rows, columns, fname = "mlb_range_of_outcomes.csv") {
  const header = columns.map((c) => c.label).join(",");
  const body = rows
    .map((r) =>
      columns
        .map((c) => {
          const v = r[c.key];
          if (c.type === "money") return escapeCSV(fmtMoney0(v));
          if (c.type === "pct") return escapeCSV(fmtPct1(v));
          if (c.type === "num1") return escapeCSV(fmt1(v));
          return escapeCSV(v ?? "");
        })
        .join(",")
    )
    .join("\n");
  const blob = new Blob([header + "\n" + body], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = fname;
  a.click();
  URL.revokeObjectURL(url);
}
function collapseBinsForBatters(bins) {
  const parsed = cleanBins(bins);
  let tailProb = 0;
  const head = [];
  for (const b of parsed) {
    if (b.bin_start >= 20) tailProb += b.prob;
    else head.push(b);
  }
  if (tailProb > 0) head.push({ bin_start: 20, bin_end: 9999, prob: tailProb, _label: "20+" });
  return head;
}
function PointDistModal({ open, onClose, isDark, site, playerName, bins, proj, isBatter }) {
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth < 640 : false
  );
  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const handleResize = () => setIsMobile(window.innerWidth < 640);
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);
  if (!open) return null;
  const finalBins = isBatter ? collapseBinsForBatters(bins) : cleanBins(bins);
  const chartData = finalBins.map((b, idx) => {
    const a = Number(b?.bin_start);
    const z = Number(b?.bin_end);
    const p = Number(b?.prob);
    const label = b?._label || (Number.isFinite(a) && Number.isFinite(z) ? `${a}-${z}` : `bin-${idx + 1}`);
    const mid = label === "20+" ? 20 : Number.isFinite(a) && Number.isFinite(z) ? (a + z) / 2 : idx;
    return { label, mid, bin_start: a, bin_end: z, probPct: Number.isFinite(p) ? p * 100 : 0 };
  });
  const projX = (() => {
    if (!Number.isFinite(proj)) return null;
    if (isBatter && proj >= 20) return 20;
    return proj;
  })();
  const xDomain = (() => {
    const mids = chartData
      .map((d) => d.mid)
      .filter(Number.isFinite)
      .sort((a, b) => a - b);
    if (!mids.length) return [0, 1];
    if (mids.length === 1) return [mids[0] - 2.5, mids[0] + 2.5];
    const firstGap = Math.abs(mids[1] - mids[0]) || 1;
    const lastGap = Math.abs(mids[mids.length - 1] - mids[mids.length - 2]) || firstGap;
    return [
      mids[0] - firstGap / 2,
      mids[mids.length - 1] + lastGap / 2,
    ];
  })();
  const xTicks = chartData
    .filter((_, i) => {
      if (isBatter) return true;
      return isMobile ? i % 4 === 0 : i % 2 === 0;
    })
    .map((d) => d.mid)
    .filter(Number.isFinite);
  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center p-2 sm:p-4"
      role="dialog"
      aria-modal="true"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose?.();
      }}
    >
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
      <div
        className={[
          "relative w-full max-w-3xl max-h-[95vh] rounded-2xl border shadow-2xl overflow-y-auto",
          isDark
            ? "border-slate-700/60 bg-slate-950 text-slate-100"
            : "border-slate-200 bg-white text-slate-900",
        ].join(" ")}
      >
        <div className={isDark ? "bg-slate-950/95" : "bg-white"}>
          <div className="flex items-start justify-between gap-4 px-5 pt-5">
            <div className="min-w-0">
              <div className={isDark ? "text-xs text-slate-400" : "text-xs text-slate-500"}>
                Fantasy Points Distribution
              </div>
              <div className="mt-1 flex flex-wrap items-center gap-2">
                <div className="text-lg font-extrabold tracking-tight truncate">{playerName || "—"}</div>
                <span
                  className={[
                    "inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-bold tracking-wide",
                    site === "DK"
                      ? isDark
                        ? "bg-emerald-500/15 text-emerald-200 border border-emerald-500/30"
                        : "bg-emerald-50 text-emerald-800 border border-emerald-200"
                      : isDark
                      ? "bg-sky-500/15 text-sky-200 border border-sky-500/30"
                      : "bg-sky-50 text-sky-800 border border-sky-200",
                  ].join(" ")}
                >
                  {site}
                </span>
                {Number.isFinite(proj) ? (
                  <span className={isDark ? "text-xs text-slate-300" : "text-xs text-slate-600"}>
                    Proj: <span className="font-semibold tabular-nums">{proj.toFixed(1)}</span>
                  </span>
                ) : null}
              </div>
            </div>
            <button
              className={[
                "shrink-0 rounded-xl border px-3 py-2 text-sm font-semibold",
                isDark
                  ? "border-slate-700 bg-slate-900/40 hover:bg-slate-900"
                  : "border-slate-200 bg-slate-50 hover:bg-slate-100",
              ].join(" ")}
              onClick={onClose}
            >
              Close
            </button>
          </div>
          <div className="px-5 pb-5 pt-4">
            {!chartData.length ? (
              <div className={isDark ? "text-sm text-slate-300" : "text-sm text-slate-700"}>
                No distribution data found for this player yet.
              </div>
            ) : (
              <div className={isMobile ? "h-[360px] w-full" : "h-[320px] w-full"}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={chartData}
                    margin={{
                      top: 10,
                      right: 18,
                      left: 8,
                      bottom: isMobile ? 42 : 18,
                    }}
                    barCategoryGap={isMobile ? "12%" : "8%"}
                  >
                    <CartesianGrid stroke={isDark ? "#334155" : "#e5e7eb"} strokeDasharray="3 3" />
                    <XAxis
                      type="number"
                      dataKey="mid"
                      domain={xDomain}
                      ticks={xTicks}
                      tickFormatter={(v) => {
                        const hit = chartData.find((d) => d.mid === v);
                        if (hit?.label === "20+") return "20+";
                        return `${Math.round(v)}`;
                      }}
                      tick={{
                        fontSize: isMobile ? 11 : 12,
                        fill: isDark ? "#cbd5f5" : "#334155",
                      }}
                      axisLine={{ stroke: isDark ? "#475569" : "#cbd5e1" }}
                      tickLine={false}
                      angle={isMobile ? -35 : 0}
                      textAnchor={isMobile ? "end" : "middle"}
                      height={isMobile ? 52 : 30}
                    />
                    <YAxis
                      tickFormatter={(v) => `${v}%`}
                      tick={{ fontSize: 11, fill: isDark ? "#cbd5f5" : "#334155" }}
                      axisLine={{ stroke: isDark ? "#475569" : "#cbd5e1" }}
                      tickLine={false}
                    />
                    <Tooltip
                      cursor={{ fill: "rgba(56,189,248,0.12)" }}
                      contentStyle={{
                        backgroundColor: isDark ? "#020617" : "#ffffff",
                        border: "1px solid #38bdf8",
                        borderRadius: 10,
                        fontSize: 12,
                      }}
                      labelStyle={{ color: isDark ? "#e2e8f0" : "#0f172a" }}
                      formatter={(v) => [`${Number(v).toFixed(2)}%`, "Probability"]}
                      labelFormatter={(_, payload) => {
                        const p = payload?.[0]?.payload;
                        return p?.label ? `Bin ${p.label}` : "";
                      }}
                    />
                    {Number.isFinite(projX) ? (
                      <ReferenceLine x={projX} stroke="#38bdf8" strokeWidth={2} strokeDasharray="6 4" />
                    ) : null}
                    <Bar dataKey="probPct" radius={[6, 6, 0, 0]} fill="#7dd3fc" stroke="#38bdf8" strokeWidth={1} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
            {isBatter ? (
              <div className={isDark ? "mt-3 text-xs text-slate-400" : "mt-3 text-xs text-slate-500"}>
                Batter bins are capped to <span className="font-semibold">20+</span> for readability.
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}
export default function MlbRangeOfOutcomes() {
  const { rows, loading, err } = useMlbCombined(BATTERS_SOURCE, PITCHERS_SOURCE);
  const { data: siteIdRaw } = useAnyJson(SITE_ID_SOURCE);
  const { data: batterPointDistsRaw } = useAnyJson(BATTER_POINT_DISTS);
  const { data: pitcherPointDistsRaw } = useAnyJson(PITCHER_POINT_DISTS);
  const [site, setSite] = useState("dk");
  const [slate, setSlate] = useState("all");
  const [kind, setKind] = useState("both");
  const [pos, setPos] = useState("ALL");
  const [theme, setTheme] = useState("dark");
  const [q, setQ] = useState("");
  const [sort, setSort] = useState({ key: "roScore", dir: "desc" });
  const [modal, setModal] = useState({ open: false, row: null, siteKey: "dk", bins: [] });
  const isDark = theme === "dark";
  const siteIdIndex = useMemo(() => buildSiteIdIndex(siteIdRaw), [siteIdRaw]);
  const batterDistIndex = useMemo(() => buildDistIndex(batterPointDistsRaw), [batterPointDistsRaw]);
  const pitcherDistIndex = useMemo(() => buildDistIndex(pitcherPointDistsRaw), [pitcherPointDistsRaw]);
  const updatedText = useLastUpdatedFromSources(BATTERS_SOURCE, PITCHERS_SOURCE);
  const posOptions = useMemo(() => ["ALL", "P", "C", "1B", "2B", "3B", "SS", "OF"], []);
  const filteredBase = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return rows.filter((r) => {
      if (kind === "bat" && r.type !== "BAT") return false;
      if (kind === "p" && r.type !== "P") return false;
      if (!rowMatchesSlate(siteIdIndex, r, site, slate)) return false;
      if (pos !== "ALL") {
        const parts = normalizeParts(getPosForSite(r, site));
        if (!parts.includes(pos)) return false;
      }
      if (!needle) return true;
      return `${r.player ?? ""} ${r.team ?? ""} ${r.opp ?? ""} ${getPosForSite(r, site)} ${r.type ?? ""}`
        .toLowerCase()
        .includes(needle);
    });
  }, [rows, kind, siteIdIndex, site, slate, pos, q]);
  const rangeRows = useMemo(() => {
    const out = [];
    for (const row of filteredBase) {
      for (const siteKey of siteKeysFor(site)) {
        const salary = getRenderedSalary(siteIdIndex, row, siteKey, slate, row[`${siteKey}_sal`]);
        const proj = row[`${siteKey}_proj`];
        const floor = row[`${siteKey}_floor`];
        const ceiling = row[`${siteKey}_ceiling`];
        const pown = row[`${siteKey}_pown`];
        const opt = row[`${siteKey}_optimal`];
        const val = row[`${siteKey}_val`] ?? (salary ? proj / (salary / 1000) : null);
        const rtg = row[`${siteKey}_rtg`];
        if (!salary || !proj) continue;
        const idx = row.type === "BAT" ? batterDistIndex : pitcherDistIndex;
        const rawBins = idx?.[siteKey]?.[keynorm(row.player)] || [];
        const fallbackBins = buildBinsFromFloorCeil(proj, floor, ceiling, row.type === "BAT");
        const bins = cleanBins(rawBins).length ? rawBins : fallbackBins;
        const hit = {};
        for (const m of MULTS) {
          const key = `x${String(m).replace(".", "_")}`;
          hit[key] = probGte(bins, (salary / 1000) * m);
        }
        const {
          goodTarget,
          boomTarget,
          smashTarget,
          bustTarget,
          goodLabel,
          boomLabel,
          smashLabel,
          bustLabel,
        } = getOutcomeTargets(row, siteKey, salary);
        const good = probGte(bins, goodTarget);
        const boom = probGte(bins, boomTarget);
        const smash = probGte(bins, smashTarget);
        const bust = probLte(bins, bustTarget);
        const lev = opt !== null && pown !== null ? opt - pown : null;
        const metrics = {
          ...hit,
          good,
          boom,
          smash,
          bust,
          pown,
          opt,
          lev,
          val,
          rtg,
        };
        out.push({
          _row: row,
          _bins: bins,
          type: row.type,
          site: siteKey.toUpperCase(),
          player: row.player,
          pos: getPosForSite(row, siteKey),
          team: row.team,
          opp: row.opp,
          sal: salary,
          proj,
          val,
          pown,
          opt,
          lev,
          rtg,
          x1: hit.x1,
          x1_5: hit.x1_5,
          x2: hit.x2,
          x2_5: hit.x2_5,
          x3: hit.x3,
          x4: hit.x4,
          good,
          boom,
          smash,
          bust,
          goodTag: goodLabel,
          boomTag: boomLabel,
          smashTag: smashLabel,
          bustTag: bustLabel,
          roScore: rangeScore(row, metrics),
          distSource: cleanBins(rawBins).length ? "File" : fallbackBins.length ? "Est" : "—",
        });
      }
    }
    return out;
  }, [filteredBase, site, siteIdIndex, slate, batterDistIndex, pitcherDistIndex]);
  const columns = useMemo(
    () => [
      { key: "site", label: "Site", type: "text" },
      { key: "type", label: "Type", type: "text" },
      { key: "pos", label: "Pos", type: "text" },
      { key: "player", label: "Player", type: "text", sticky: true },
      { key: "team", label: "Team", type: "text" },
      { key: "opp", label: "Opp", type: "text" },
      { key: "sal", label: "Sal", type: "money" },
      { key: "proj", label: "Proj", type: "num1" },
      { key: "val", label: "Val", type: "num1" },
      { key: "x1", label: "1x", type: "pct" },
      { key: "x1_5", label: "1.5x", type: "pct" },
      { key: "x2", label: "2x", type: "pct" },
      { key: "x2_5", label: "2.5x", type: "pct" },
      { key: "x3", label: "3x", type: "pct" },
      { key: "x4", label: "4x", type: "pct" },
      { key: "good", label: "Good", type: "pct" },
      { key: "boom", label: "Boom", type: "pct" },
      { key: "smash", label: "Smash", type: "pct" },
      { key: "bust", label: "Bust", type: "pct" },
      { key: "roScore", label: "RO Score", type: "num1" },
      { key: "pown", label: "pOWN%", type: "pct" },
      { key: "opt", label: "Opt%", type: "pct" },
      { key: "lev", label: "Lev%", type: "pct" },
      { key: "rtg", label: "Rtg", type: "num1" },
    ],
    []
  );
  const stats = useMemo(() => {
    const s = {};
    for (const c of columns) {
      const dir = dirForKey(c.key);
      if (!dir) continue;
      const vals = rangeRows.map((r) => num(r[c.key])).filter((v) => v !== null);
      if (vals.length > 1) s[c.key] = { min: Math.min(...vals), max: Math.max(...vals), dir };
    }
    return s;
  }, [rangeRows, columns]);
  const sorted = useMemo(() => {
    const arr = [...rangeRows];
    const dir = sort.dir === "asc" ? 1 : -1;
    arr.sort((a, b) => {
      const av = a[sort.key];
      const bv = b[sort.key];
      const an = num(av);
      const bn = num(bv);
      if (an !== null || bn !== null) {
        if (an === null) return 1;
        if (bn === null) return -1;
        return (an - bn) * dir;
      }
      return String(av ?? "").localeCompare(String(bv ?? "")) * dir;
    });
    return arr;
  }, [rangeRows, sort]);
  const onSort = (c) => {
    setSort((prev) =>
      prev.key === c.key ? { key: c.key, dir: prev.dir === "desc" ? "asc" : "desc" } : { key: c.key, dir: "desc" }
    );
  };
  const openDist = (r) => {
    setModal({ open: true, row: r, siteKey: r.site.toLowerCase(), bins: r._bins });
  };
  const outerClass = isDark ? "min-h-screen bg-slate-950 text-slate-100" : "min-h-screen bg-slate-100 text-slate-900";
  const cardClass = isDark
    ? "rounded-2xl border border-slate-800 bg-slate-900/95 shadow-sm overflow-hidden"
    : "rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden";
  const headerBgClass = isDark ? "bg-slate-900 text-slate-200" : "bg-gray-50 text-gray-800";
  return (
    <div className={outerClass}>
      <div className="w-full px-4 md:px-6 py-5">
        <header className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <div className="flex min-w-0 flex-wrap items-baseline gap-x-3 gap-y-1">
            <h1 className="text-xl font-bold tracking-tight">MLB — Range of Outcomes</h1>
            <span className={"text-xs font-normal italic " + (isDark ? "text-slate-100" : "text-slate-700")}>Updated: {updatedText || "—"}</span>
          </div>
            <div className={"inline-flex items-center rounded-full px-1 py-1 text-[11px] ring-1 " + (isDark ? "bg-slate-900/90 ring-slate-700/80" : "bg-white ring-slate-300")}>
              <button
                type="button"
                onClick={() => setTheme("dark")}
                className={"px-3 py-1 rounded-full font-medium transition " + (isDark ? "bg-sky-500 text-slate-950 shadow-sm" : "text-slate-500 hover:text-slate-800")}
              >
                Dark
              </button>
              <button
                type="button"
                onClick={() => setTheme("light")}
                className={"px-3 py-1 rounded-full font-medium transition " + (!isDark ? "bg-sky-500 text-slate-950 shadow-sm" : "text-slate-400 hover:text-slate-100")}
              >
                Light
              </button>
            </div>
        </header>
        <section aria-label="Site and player filters" className={cardClass + " mb-4"}>
          <div className="flex flex-wrap items-center gap-x-5 gap-y-3 px-4 py-3">
            <div className={"inline-flex flex-wrap items-center gap-1 border-b " + (isDark ? "border-slate-800" : "border-slate-200")}>
              {Object.values(SITES).map((x) => (
                <button
                  key={x.key}
                  onClick={() => setSite(x.key)}
                  className={`h-9 px-3 border-b-2 text-xs font-semibold inline-flex items-center gap-1.5 transition ${
                    site === x.key ? (isDark ? "border-sky-400 text-white" : "border-sky-500 text-slate-900") : isDark ? "border-transparent text-slate-400 hover:text-white" : "border-transparent text-slate-500 hover:text-slate-900"
                  }`}
                >
                  {x.logo ? <img src={x.logo} alt={x.label} className="w-4 h-4" /> : null}
                  <span>{x.label}</span>
                </button>
              ))}
            </div>
            <div className={"inline-flex flex-wrap items-center gap-1 border-b " + (isDark ? "border-slate-800" : "border-slate-200")}>
              {[
                { k: "both", label: "Both" },
                { k: "bat", label: "Batters" },
                { k: "p", label: "Pitchers" },
              ].map((x) => (
                <button
                  key={x.k}
                  onClick={() => setKind(x.k)}
                  className={`h-9 px-3 border-b-2 text-xs font-semibold transition ${
                    kind === x.k ? (isDark ? "border-sky-400 text-white" : "border-sky-500 text-slate-900") : isDark ? "border-transparent text-slate-400 hover:text-white" : "border-transparent text-slate-500 hover:text-slate-900"
                  }`}
                >
                  {x.label}
                </button>
              ))}
            </div>
            <div className={"inline-flex flex-wrap items-center gap-1 border-b " + (isDark ? "border-slate-800" : "border-slate-200")}>
              {[
                { k: "all", label: "All" },
                { k: "early", label: "Early" },
                { k: "main", label: "Main" },
                { k: "late", label: "Late" },
              ].map((x) => (
                <button
                  key={x.k}
                  onClick={() => setSlate(x.k)}
                  className={`h-9 px-3 border-b-2 text-xs font-semibold transition ${
                    slate === x.k ? (isDark ? "border-sky-400 text-white" : "border-sky-500 text-slate-900") : isDark ? "border-transparent text-slate-400 hover:text-white" : "border-transparent text-slate-500 hover:text-slate-900"
                  }`}
                >
                  {x.label}
                </button>
              ))}
            </div>
          </div>
        </section>
        <section aria-label="Player outcomes" className={cardClass}>
          <div className={"flex flex-wrap items-center justify-between gap-3 border-b px-4 py-3 " + (isDark ? "border-slate-800" : "border-slate-200")}>
            <div>
              <h2 className="text-sm font-semibold">Player Outcomes</h2>
              <p className={"mt-1 text-[11px] " + (isDark ? "text-slate-400" : "text-slate-500")}>Click a projection for its point distribution.</p>
            </div>
            <button
              className={"h-8 px-3 rounded-md text-xs font-semibold transition " + (isDark ? "bg-sky-500 text-slate-950 hover:bg-sky-400" : "bg-blue-600 text-white hover:bg-blue-700")}
              onClick={() => downloadCSV(sorted, columns)}
            >
              Export CSV
            </button>
          </div>
          <div className="px-4 pt-2">
        <div className="flex flex-wrap items-center gap-1 border-b border-slate-700/30">
          {posOptions.map((p) => (
            <button
              key={p}
              onClick={() => setPos(p)}
              className={`h-9 px-2.5 border-b-2 text-xs font-semibold transition ${
                pos === p
                  ? (isDark ? "border-sky-400 text-white" : "border-sky-500 text-slate-900")
                  : isDark
                  ? "border-transparent text-slate-400 hover:text-white"
                  : "border-transparent text-slate-500 hover:text-slate-900"
              }`}
            >
              {p}
            </button>
          ))}
        </div>
          </div>
          <div className="px-4 py-3">
            <input
              className={"h-8 w-full sm:w-72 rounded-md border px-3 text-xs focus:outline-none focus:ring-2 focus:ring-sky-500 " +
                (isDark ? "bg-slate-950/80 border-slate-700 text-slate-100 placeholder:text-slate-500" : "bg-white border-gray-300 text-gray-900 placeholder:text-gray-400")}
              placeholder="Search player / team / opp…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
          </div>

          <div className="overflow-x-auto">
            <div className="max-h-[72vh] overflow-y-auto">
              <table className="border-separate table-auto text-[12px]" style={{ borderSpacing: 0, width: "max-content", minWidth: "100%" }}>
                <thead className={headerBgClass + " sticky top-0 z-40"}>
                  <tr>
                    {columns.map((c) => {
                      const stickyStyle = c.sticky
                        ? { left: 0, minWidth: 220, width: 220, maxWidth: 220, backgroundColor: isDark ? "#0f172a" : "#f9fafb" }
                        : undefined;
                      return (
                        <th
                          key={c.key}
                          className={`px-1.5 py-1 font-semibold text-center whitespace-nowrap cursor-pointer select-none ${c.sticky ? "sticky z-50" : ""}`}
                          style={stickyStyle}
                          onClick={() => onSort(c)}
                        >
                          <span>{c.label}</span>
                          <span className={isDark ? "ml-1 text-slate-400" : "ml-1 text-gray-400"}>
                            {sort.key === c.key ? (sort.dir === "desc" ? "▼" : "▲") : "▲"}
                          </span>
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  {loading && (
                    <tr>
                      <td colSpan={columns.length} className="px-2 py-4 text-center text-slate-400">Loading…</td>
                    </tr>
                  )}
                  {!loading &&
                    sorted.map((r, i) => {
                      const rowBg = i % 2 === 0 ? (isDark ? "bg-slate-950" : "bg-white") : isDark ? "bg-slate-900" : "bg-gray-50";
                      return (
                        <tr key={`${r.site}-${r.type}-${r.player}-${r.team}-${i}`} className={rowBg + " " + (isDark ? "hover:bg-slate-900/80" : "hover:bg-blue-50")}>
                          {columns.map((c) => {
                            const value = r[c.key];
                            const stat = stats[c.key];
                            const color = stat ? heatTextColor(stat.min, stat.max, value, stat.dir, isDark) : null;
                            const isProj = c.key === "proj";
                            const stickyStyle = c.sticky
                              ? { left: 0, minWidth: 220, width: 220, maxWidth: 220 }
                              : undefined;
                            const stickyCls = c.sticky
                              ? `px-2 py-1 sticky z-30 ${rowBg} shadow-[inset_-6px_0_6px_-6px_rgba(0,0,0,0.35)] text-left`
                              : "px-1.5 py-0.5 text-center whitespace-nowrap tabular-nums";
                            let content = value;
                            if (c.type === "money") content = fmtMoney0(value);
                            if (c.type === "pct") content = fmtPct1(value);
                            if (c.type === "num1") content = fmt1(value);
                            return (
                              <td key={c.key} className={stickyCls} style={stickyStyle} title={String(content ?? "")}>
                                {c.key === "player" ? (
                                  <div className="flex items-center justify-start gap-2">
                                    <span className="w-5 shrink-0 flex items-center justify-center">
                                      <img
                                        src={teamLogo(r.team)}
                                        alt=""
                                        className="w-4 h-4 rounded-sm object-contain"
                                        onError={(e) => {
                                          e.currentTarget.style.visibility = "hidden";
                                        }}
                                      />
                                    </span>
                                    <span className="whitespace-nowrap truncate">{r.player}</span>
                                  </div>
                                ) : isProj ? (
                                  <button
                                    className="group relative w-full text-center font-semibold rounded-md px-1 py-0.5 underline underline-offset-4 decoration-sky-400/70 hover:decoration-sky-300 hover:bg-sky-500/10 transition cursor-pointer"
                                    style={color ? { color, fontWeight: 700 } : undefined}
                                    onClick={() => openDist(r)}
                                    title="Click to open point distribution chart"
                                  >
                                    {content}
                                  </button>
                                ) : (
                                  <span style={color ? { color, fontWeight: 600 } : undefined}>{content}</span>
                                )}
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })}
                  {!loading && !sorted.length && (
                    <tr>
                      <td colSpan={columns.length} className="px-2 py-4 text-center text-slate-400">No data.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </section>
        <div className={"mt-2 text-[12px] flex flex-wrap gap-3 " + (isDark ? "text-slate-500" : "text-gray-500")}>
          <span>Multiplier columns = salary / 1000 × multiplier</span>
          <span>Good/Boom/Smash use raw-point floors plus salary-adjusted targets</span>
          <span>Cheap players must clear real point thresholds to count as boom plays</span>
          <span>Distribution files: batter_point_distributions.json + pitcher_point_distributions.json</span>
        </div>
      </div>
      <PointDistModal
        open={modal.open}
        onClose={() => setModal({ open: false, row: null, siteKey: "dk", bins: [] })}
        isDark={isDark}
        site={modal.siteKey?.toUpperCase?.() || "DK"}
        playerName={modal.row?.player || ""}
        isBatter={modal.row?.type === "BAT"}
        proj={num(modal.row?.proj)}
        bins={modal.bins}
      />
    </div>
  );
}
